/**
 * useAnalysis Hook
 * Manages code analysis, refactoring, and transformation operations
 */

import { useState, useCallback } from 'react'
import { useApp } from '../context'
import { JobType, JobStatus } from '../core/entities/Job'

export function useAnalysis(applicationId) {
  const { db, api } = useApp()
  const [analyzing, setAnalyzing] = useState(false)
  const [progress, setProgress] = useState(0)
  const [currentJob, setCurrentJob] = useState(null)
  const [results, setResults] = useState(null)
  const [error, setError] = useState(null)

  // Helper: Get application details
  const getAppDetails = useCallback(async () => {
    const app = await db.getApplication(applicationId)
    if (!app) throw new Error('Application not found')
    return app
  }, [db, applicationId])

  // Helper: Poll until complete
  const pollUntilComplete = useCallback(async (jobId, statusFn, onProgress) => {
    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          const status = await statusFn(jobId)
          if (onProgress) onProgress(status)

          if (status.status === JobStatus.COMPLETED || status.status === 'completed') {
            resolve(status)
          } else if (status.status === JobStatus.FAILED || status.status === 'failed') {
            reject(new Error(status.message || 'Job failed'))
          } else {
            setTimeout(poll, 1000)
          }
        } catch (err) {
          reject(err)
        }
      }
      poll()
    })
  }, [])

  // ===== Code Analysis =====

  const runCodeAnalysis = useCallback(async ({
    generateJava = true,
    generateGraphs = true,
    onProgress,
  } = {}) => {
    setAnalyzing(true)
    setError(null)
    setProgress(0)
    setResults(null)

    try {
      const app = await getAppDetails()

      // Start job
      setProgress(10)
      const { jobId } = await api.startCodeAnalysis({
        scoutAccountId: app.scoutAccountId,
        applicationName: app.name,
        generateJava,
        generateGraphs,
      })

      // Track job
      const job = await db.createJob({
        id: jobId,
        type: JobType.CODE_ANALYSIS,
        applicationId,
        scoutAccountId: app.scoutAccountId,
        applicationName: app.name,
      })
      setCurrentJob(job)

      // Poll
      setProgress(20)
      await pollUntilComplete(jobId, api.getCodeAnalysisStatus, (status) => {
        const p = 20 + (status.progress * 0.6)
        setProgress(p)
        if (onProgress) onProgress({ stage: 'analyzing', progress: p, status })
      })

      // Get results
      setProgress(90)
      const analysisResults = await api.getCodeAnalysisResults(jobId)

      await db.updateJob(jobId, {
        status: JobStatus.COMPLETED,
        progress: 100,
        results: analysisResults,
      })

      setProgress(100)
      setResults(analysisResults)
      return { jobId, results: analysisResults }
    } catch (err) {
      setError(err.message)
      throw err
    } finally {
      setAnalyzing(false)
    }
  }, [db, api, applicationId, getAppDetails, pollUntilComplete])

  // ===== Code Refactor =====

  const runRefactorAnalysis = useCallback(async ({
    useAI = true,
    onProgress,
  } = {}) => {
    setAnalyzing(true)
    setError(null)
    setProgress(0)

    try {
      const app = await getAppDetails()

      const { jobId } = await api.startCodeRefactor({
        scoutAccountId: app.scoutAccountId,
        applicationName: app.name,
        mode: 'analyze',
        useAI,
      })

      const job = await db.createJob({
        id: jobId,
        type: JobType.CODE_REFACTOR,
        applicationId,
        scoutAccountId: app.scoutAccountId,
        applicationName: app.name,
      })
      setCurrentJob(job)

      await pollUntilComplete(jobId, api.getCodeRefactorStatus, (status) => {
        const p = 10 + (status.progress * 0.7)
        setProgress(p)
        if (onProgress) onProgress({ stage: 'analyzing', progress: p, status })
      })

      const refactorResults = await api.getCodeRefactorResults(jobId)
      const recipes = await api.getCodeRefactorRecipes(jobId)

      setProgress(100)
      setResults({ ...refactorResults, recipes })
      return { jobId, results: refactorResults, recipes }
    } catch (err) {
      setError(err.message)
      throw err
    } finally {
      setAnalyzing(false)
    }
  }, [db, api, applicationId, getAppDetails, pollUntilComplete])

  const transformCode = useCallback(async ({
    recipes = 'all',
    onProgress,
  } = {}) => {
    setAnalyzing(true)
    setError(null)
    setProgress(0)

    try {
      const app = await getAppDetails()

      const { jobId } = await api.transformCode({
        scoutAccountId: app.scoutAccountId,
        applicationName: app.name,
        recipesToApply: recipes,
      })

      await pollUntilComplete(jobId, api.getCodeRefactorStatus, (status) => {
        const p = 10 + (status.progress * 0.8)
        setProgress(p)
        if (onProgress) onProgress({ stage: 'transforming', progress: p, status })
      })

      const results = await api.getCodeRefactorResults(jobId)
      setProgress(100)
      setResults(results)
      return { jobId, results }
    } catch (err) {
      setError(err.message)
      throw err
    } finally {
      setAnalyzing(false)
    }
  }, [db, api, applicationId, getAppDetails, pollUntilComplete])

  // ===== Dependency Mapping =====

  const runDependencyMapping = useCallback(async ({
    sourceType = 'cobol',
    onProgress,
  } = {}) => {
    setAnalyzing(true)
    setError(null)
    setProgress(0)

    try {
      const app = await getAppDetails()

      const { jobId } = await api.startDependencyMapper({
        scoutAccountId: app.scoutAccountId,
        applicationName: app.name,
        sourceType,
      })

      await pollUntilComplete(jobId, api.getDependencyMapperStatus, (status) => {
        setProgress(10 + (status.progress * 0.8))
        if (onProgress) onProgress(status)
      })

      const results = await api.getDependencyMapperResults(jobId)
      setProgress(100)
      setResults(results)
      return { jobId, results }
    } catch (err) {
      setError(err.message)
      throw err
    } finally {
      setAnalyzing(false)
    }
  }, [api, getAppDetails, pollUntilComplete])

  // ===== Monolith Identification =====

  const runMonolithIdentification = useCallback(async ({
    sourceType = 'cobol',
    onProgress,
  } = {}) => {
    setAnalyzing(true)
    setError(null)
    setProgress(0)

    try {
      const app = await getAppDetails()

      const { jobId } = await api.startMonolithIdentifier({
        scoutAccountId: app.scoutAccountId,
        applicationName: app.name,
        sourceType,
      })

      await pollUntilComplete(jobId, api.getMonolithIdentifierStatus, (status) => {
        setProgress(10 + (status.progress * 0.8))
        if (onProgress) onProgress(status)
      })

      const results = await api.getMonolithIdentifierResults(jobId)
      setProgress(100)
      setResults(results)
      return { jobId, results }
    } catch (err) {
      setError(err.message)
      throw err
    } finally {
      setAnalyzing(false)
    }
  }, [api, getAppDetails, pollUntilComplete])

  // ===== Data Analysis =====

  const runDataAnalysis = useCallback(async ({ onProgress } = {}) => {
    setAnalyzing(true)
    setError(null)
    setProgress(0)

    try {
      const app = await getAppDetails()

      const { jobId } = await api.startDataAnalysis({
        scoutAccountId: app.scoutAccountId,
        applicationName: app.name,
      })

      await pollUntilComplete(jobId, api.getDataAnalysisStatus, (status) => {
        setProgress(10 + (status.progress * 0.7))
        if (onProgress) onProgress(status)
      })

      const results = await api.getDataAnalysisResults(jobId)
      const erd = await api.getERD(jobId)
      const lineage = await api.getDataLineage(jobId)

      setProgress(100)
      setResults({ ...results, erd, lineage })
      return { jobId, results, erd, lineage }
    } catch (err) {
      setError(err.message)
      throw err
    } finally {
      setAnalyzing(false)
    }
  }, [api, getAppDetails, pollUntilComplete])

  // ===== Discovery =====

  const runDiscovery = useCallback(async ({
    enableAI = true,
    onProgress,
  } = {}) => {
    setAnalyzing(true)
    setError(null)
    setProgress(0)

    try {
      const app = await getAppDetails()

      const { jobId } = await api.startDiscovery({
        scoutAccountId: app.scoutAccountId,
        applicationName: app.name,
        enableAI,
      })

      // Discovery doesn't have a status endpoint in the same way
      // Simulate progress
      for (let i = 0; i < 10; i++) {
        await new Promise(r => setTimeout(r, 500))
        setProgress(10 + (i * 8))
        if (onProgress) onProgress({ progress: 10 + (i * 8) })
      }

      const results = await api.getDiscoveryResults(jobId)
      setProgress(100)
      setResults(results)
      return { jobId, results }
    } catch (err) {
      setError(err.message)
      throw err
    } finally {
      setAnalyzing(false)
    }
  }, [api, getAppDetails])

  // ===== Architecture =====

  const runArchitectureAnalysis = useCallback(async ({
    generateIaC = true,
    previousJobIds = {},
    onProgress,
  } = {}) => {
    setAnalyzing(true)
    setError(null)
    setProgress(0)

    try {
      const app = await getAppDetails()

      const { jobId } = await api.startArchitecture({
        scoutAccountId: app.scoutAccountId,
        applicationName: app.name,
        generateIaC,
        ...previousJobIds,
      })

      await pollUntilComplete(jobId, api.getArchitectureStatus, (status) => {
        setProgress(10 + (status.progress * 0.7))
        if (onProgress) onProgress(status)
      })

      const results = await api.getArchitectureResults(jobId)
      let iacTemplates = []
      if (generateIaC) {
        iacTemplates = await api.getIaCTemplates(jobId)
      }

      setProgress(100)
      setResults({ ...results, iacTemplates })
      return { jobId, results, iacTemplates }
    } catch (err) {
      setError(err.message)
      throw err
    } finally {
      setAnalyzing(false)
    }
  }, [api, getAppDetails, pollUntilComplete])

  // Reset state
  const reset = useCallback(() => {
    setAnalyzing(false)
    setProgress(0)
    setCurrentJob(null)
    setResults(null)
    setError(null)
  }, [])

  return {
    // State
    analyzing,
    isProcessing: analyzing,
    progress,
    currentJob,
    results,
    error,

    // Actions
    runCodeAnalysis,
    runRefactorAnalysis,
    transformCode,
    runDependencyMapping,
    runMonolithIdentification,
    runDataAnalysis,
    runDiscovery,
    runArchitectureAnalysis,
    reset,
  }
}

export default useAnalysis
